<template>
  <b-modal
    ref="detail-modal"
    title="Chi tiết thay đổi"
    size="xl"
    hide-footer
    scrollable
  >
    <div v-if="onLoading" class="text-center py-4">
      <b-spinner small variant="primary" />
    </div>
    <template v-else-if="detail">
      <dl class="row mb-3">
        <dt class="col-5 col-md-3 col-lg-2">Chương trình</dt>
        <dd class="col-7 col-md-9 col-lg-10 text-break">
          {{ detail.programName }}
          <span
            v-if="detail.isProgramDeleted"
            class="label label-light-dark label-inline ml-1"
            >Đã xóa</span
          >
        </dd>
        <dt class="col-5 col-md-3 col-lg-2">Hành động</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ actionLabel }}</dd>
        <dt class="col-5 col-md-3 col-lg-2">Người thực hiện</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ detail.updatedByName || '—' }}</dd>
        <dt class="col-5 col-md-3 col-lg-2">Thời gian</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ formatTime(detail.createdAt) }}</dd>
      </dl>

      <p v-if="isDelete" class="font-weight-bold mb-0">
        {{ messages.PROGRAM_DELETED }}
      </p>
      <template v-else>
        <template v-if="detail.diffValues.length">
          <h6 class="font-weight-bold">Thông tin chung</h6>
          <b-table
            :items="detail.diffValues"
            :fields="headerFields"
            bordered
            small
            responsive
            class="mb-4"
          >
            <template #cell(originalValue)="{ value }">
              <span class="text-break">{{ value || '' }}</span>
            </template>
            <template #cell(newValue)="{ value }">
              <span class="text-break">{{ value || '' }}</span>
            </template>
          </b-table>
        </template>

        <template v-if="codeRows.length">
          <h6 class="font-weight-bold">Mã coupon</h6>
          <b-table
            :items="codeRows"
            :fields="codeFields"
            :per-page="CODE_PAGE_SIZE"
            :current-page="codePage"
            bordered
            small
            responsive
            class="mb-2"
          >
            <template #cell(code)="{ value }">
              <span class="text-break">{{ value }}</span>
            </template>
            <template #cell()="{ value }">{{ value || '—' }}</template>
          </b-table>
          <b-pagination
            v-if="codeRows.length > CODE_PAGE_SIZE"
            v-model="codePage"
            :total-rows="codeRows.length"
            :per-page="CODE_PAGE_SIZE"
            size="sm"
            align="right"
          />
        </template>
        <template v-else-if="detail.detailsCount">
          <h6 class="font-weight-bold">Mã coupon</h6>
          <p class="text-muted mb-4">Thêm {{ detail.detailsCount }} mã</p>
        </template>

        <template v-if="productRows.length">
          <h6 class="font-weight-bold mt-3">Sản phẩm áp dụng</h6>
          <b-table
            :items="productRows"
            :fields="productFields"
            bordered
            small
            responsive
          >
            <template #cell(name)="{ value }">
              <span class="text-break">{{ value }}</span>
            </template>
          </b-table>
        </template>
      </template>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import { COUPON_HISTORY_MESSAGES } from '@/utils/constants';
import { HISTORY_ACTION_TYPE } from '@/utils/enum';

const CODE_PAGE_SIZE = 50;
const FORBIDDEN = 403;

const ACTION_LABEL = {
  [HISTORY_ACTION_TYPE.CREATE]: 'Tạo chương trình',
  [HISTORY_ACTION_TYPE.UPDATE]: 'Sửa chương trình',
  [HISTORY_ACTION_TYPE.DELETE]: 'Xóa chương trình',
};

export default {
  name: 'CouponProgramHistoryDetail',
  data() {
    return {
      CODE_PAGE_SIZE,
      messages: COUPON_HISTORY_MESSAGES,
      detail: null,
      onLoading: false,
      codePage: 1,
      headerFields: [
        { key: 'keyName', label: 'Thông tin', thStyle: { width: '30%' } },
        { key: 'originalValue', label: 'Trước' },
        { key: 'newValue', label: 'Sau' },
      ],
      codeFields: [
        { key: 'change', label: 'Thay đổi', thStyle: { width: '15%' } },
        { key: 'code', label: 'Mã coupon' },
        { key: 'discountType', label: 'Loại giảm' },
        { key: 'discountValue', label: 'Giá trị giảm' },
        { key: 'discountMaxValue', label: 'Giảm tối đa' },
        { key: 'maxUse', label: 'Số lượt tối đa' },
        { key: 'applyMember', label: 'Thành viên áp dụng' },
      ],
      productFields: [
        { key: 'change', label: 'Thay đổi', thStyle: { width: '15%' } },
        { key: 'typeName', label: 'Loại', thStyle: { width: '15%' } },
        { key: 'code', label: 'Mã' },
        { key: 'name', label: 'Tên' },
      ],
    };
  },
  computed: {
    isDelete() {
      return this.detail && this.detail.action === HISTORY_ACTION_TYPE.DELETE;
    },
    actionLabel() {
      return this.detail ? ACTION_LABEL[this.detail.action] || '' : '';
    },
    codeRows() {
      if (!this.detail) return [];
      const { added = [], updated = [], removed = [] } =
        this.detail.detailChanges || {};
      return [
        ...added.map((item) => ({ ...item.view, change: 'Thêm' })),
        ...updated.map((item) => ({ ...item.view, change: 'Sửa' })),
        ...removed.map((item) => ({ ...item.view, change: 'Xóa' })),
      ];
    },
    productRows() {
      if (!this.detail) return [];
      const { added = [], removed = [] } = this.detail.productChanges || {};
      return [
        ...added.map((item) => ({ ...item, change: 'Thêm' })),
        ...removed.map((item) => ({ ...item, change: 'Bỏ' })),
      ].map((item) => ({ ...item, code: item.code || item.referenceId }));
    },
  },
  methods: {
    formatTime(value) {
      return value ? moment(value).format('HH:mm DD/MM/YYYY') : '';
    },
    async show(historyId) {
      this.detail = null;
      this.codePage = 1;
      this.$refs['detail-modal'].show();
      this.onLoading = true;
      try {
        const { data } = await ApiService.get(
          `v2/coupon-histories/${historyId}`,
        );
        this.detail = data.data;
      } catch (error) {
        const status = error && error.response && error.response.status;
        makeToastFaile(
          status === FORBIDDEN
            ? COUPON_HISTORY_MESSAGES.FORBIDDEN
            : COUPON_HISTORY_MESSAGES.LOAD_FAILED,
        );
        this.$refs['detail-modal'].hide();
      } finally {
        this.onLoading = false;
      }
    },
  },
};
</script>
